/**
 * SCM14 — Country-of-origin geopolitical risk rubric.
 *
 * Maps ISO-3166 alpha-2 country codes (matching the convention used in
 * `Supplier.hq.country` throughout the supplier directory — note the
 * directory uses "UK" rather than the ISO-strict "GB", so we accept
 * both as aliases) to a coarse geopolitical risk tier.
 *
 * Rubric construction
 * ───────────────────
 * The four-tier classification is a curated synthesis of three public
 * indicators, weighted toward supply-chain-relevant signals (rule of law,
 * regulatory quality, sanctions / export-control exposure) rather than
 * pure political-instability indices:
 *
 *   1. World Bank Worldwide Governance Indicators (WGI) 2023 update —
 *      specifically the "Rule of Law" and "Regulatory Quality" estimates.
 *      Percentile rank ≥ 80 broadly maps to 'low' here.
 *      Source: https://info.worldbank.org/governance/wgi/
 *
 *   2. US State Department Travel Advisory levels
 *      (Level 1 → low, Level 2 → medium, Level 3 → high, Level 4 → critical).
 *      Source: https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html
 *
 *   3. Heritage Foundation Index of Economic Freedom 2024 — used to
 *      arbitrate between 'low' and 'medium' for mid-tier OECD members.
 *      Source: https://www.heritage.org/index/
 *
 * The tier names align with the SCM6 risk-severity vocabulary so the
 * downstream `SupplierRiskAssessment.overallTier` reduction is a simple
 * max-over-component-tiers.
 *
 * Coverage
 * ────────
 * Every country code that appears in the curated supplier directory
 * (`SUPPLIER_BASELINES` in `supplyChainBaselines.ts`) is mapped here.
 * `geopoliticalRiskFor()` defaults to 'medium' for unmapped codes — see
 * the in-function comment for the rationale. Extending the rubric is a
 * matter of appending entries; the resolver is O(1) and deterministic.
 */

/**
 * Geopolitical risk levels — coarse, supply-chain-relevant tiering.
 *
 *  - 'low'      Stable OECD / G7+ democracy, strong rule of law, no
 *               material sanctions or export-control concerns for
 *               industrial equipment shipping to the US.
 *  - 'medium'   Generally stable but with at least one notable concern:
 *               trade-policy volatility, transitional governance, or
 *               concentrated political risk.
 *  - 'high'     Heightened risk — active sanctions exposure, export-
 *               control screening required, or significant rule-of-law
 *               deficiencies.
 *  - 'critical' Active conflict zone, comprehensive US sanctions, or
 *               travel-advisory level 4. Procurement should be flagged
 *               for legal review before any RFQ.
 */
export type GeopoliticalRiskLevel = 'low' | 'medium' | 'high' | 'critical';

/**
 * ISO-3166 alpha-2 country codes → risk tier.
 *
 * The supplier directory uses the non-strict "UK" code rather than the
 * ISO-correct "GB". Both are accepted by `geopoliticalRiskFor()` and
 * mapped here under the directory convention; "GB" is included as an
 * alias for callers that pass the ISO-strict form.
 */
export const GEOPOLITICAL_RISK_RUBRIC: Readonly<Record<string, GeopoliticalRiskLevel>> = {
  // ── Low — established democracies, strong rule of law ────────────────────
  // WGI rule-of-law percentile ≥ 80 + Travel Advisory L1 + Heritage "Free"/"Mostly Free".
  US: 'low',
  CA: 'low',
  UK: 'low', // directory convention
  GB: 'low', // ISO-strict alias
  IE: 'low',
  DE: 'low',
  FR: 'low',
  NL: 'low',
  BE: 'low',
  LU: 'low',
  CH: 'low',
  AT: 'low',
  DK: 'low',
  SE: 'low',
  NO: 'low',
  FI: 'low',
  IS: 'low',
  ES: 'low',
  PT: 'low',
  IT: 'low',
  JP: 'low',
  KR: 'low',
  AU: 'low',
  NZ: 'low',
  SG: 'low',

  // ── Medium — stable but with notable governance or trade-policy concerns ─
  // WGI rule-of-law percentile 50-80, OR Travel Advisory L2,
  // OR meaningful regulatory uncertainty / sanctions adjacency.
  MX: 'medium',   // Travel Advisory mixed by state, USMCA stability ✓ but cartel-related logistics risk.
  PL: 'medium',
  CZ: 'medium',
  HU: 'medium',
  SK: 'medium',
  RO: 'medium',
  GR: 'medium',
  TW: 'medium',   // Robust governance; cross-strait political risk is the medium-tier driver.
  IL: 'medium',
  AE: 'medium',
  SA: 'medium',
  IN: 'medium',
  BR: 'medium',
  ZA: 'medium',
  CL: 'medium',
  MY: 'medium',
  TH: 'medium',
  ID: 'medium',
  VN: 'medium',
  PH: 'medium',

  // ── High — active sanctions exposure or significant governance deficits ──
  CN: 'high',     // Export-control screening required for many categories; CFIUS/IEEPA exposure.
  HK: 'high',     // Special-Administrative-Region designation; flows through PRC export-control screening since 2020.
  TR: 'high',
  EG: 'high',
  PK: 'high',
  NG: 'high',

  // ── Critical — comprehensive US sanctions / active conflict / Advisory L4 ─
  RU: 'critical',
  BY: 'critical',
  IR: 'critical',
  KP: 'critical',
  SY: 'critical',
  CU: 'critical',
  VE: 'critical',
  AF: 'critical',
  MM: 'critical', // Myanmar
};

/**
 * Resolve the geopolitical risk tier for a country code.
 *
 * Accepts mixed-case input ("us", "Us", "US" all resolve to the same
 * entry) and normalises to upper-case before lookup. Unknown codes
 * default to 'medium' — a deliberately conservative middle, not 'low' —
 * so that callers see the supplier flagged for review rather than
 * silently treated as risk-free.
 *
 * Returns the tier and, for diagnostics / UI tooltips, a boolean
 * indicating whether the result came from the rubric or fell through
 * to the default.
 */
export function geopoliticalRiskFor(
  countryCode: string | null | undefined,
): { level: GeopoliticalRiskLevel; matched: boolean } {
  if (!countryCode || typeof countryCode !== 'string') {
    return { level: 'medium', matched: false };
  }
  const key = countryCode.trim().toUpperCase();
  if (!key) return { level: 'medium', matched: false };
  const hit = GEOPOLITICAL_RISK_RUBRIC[key];
  if (hit) return { level: hit, matched: true };
  // WARNING: unmapped codes intentionally default to 'medium' (not 'low').
  // Add the country to GEOPOLITICAL_RISK_RUBRIC when extending the
  // supplier directory rather than relying on this default.
  return { level: 'medium', matched: false };
}
